import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { ServiceManager } from '@jutro/services';
import { BreakpointTrackerContext } from '@jutro/layout';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { MockUpUtil } from 'gw-portals-util-js';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { useValidation } from 'gw-portals-validation-react';
import { PolicyDetailsComponent, CoverageTable, PolicyLiabilityComponent } from 'gw-capability-gateway-react';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import {SubmissionService} from 'gw-capability-gateway';
import { ModalNextProvider, Loader, Chevron } from '@jutro/components';
import DiscountComponent
    from '../../../nexus-capability-gateway-react/Components/DiscountSurchargesDisplayComponent/DiscountSurchargesDisplayComponent';
import {AdditionalInterestTable} from 'gw-capability-quoteandbind-common-react';
import config from 'app-config';
import styles from './PolicyInformationPage.module.scss';
import metadata from './PolicyInformationPage.metadata.json5';
import messages from './PolicyInformationPage.messages';

function removeInitialData(submissionVM) {
    // eslint-disable-next-line no-param-reassign
    submissionVM.value = MockUpUtil.cleanUpMockedProperties(
        _.get(submissionVM, 'value'),
        'quote.farm',
        'baseData.accountHolder.firstName',
        'baseData.accountHolder.lastName'
    );
    return submissionVM;
}

const getHeaderSection = (title, title1, title2) => {
    return (isOpen) => (
        <React.Fragment>
            <Chevron isOpen={isOpen} align="right" className={styles.chevronStyle} />
            <h3 className={`${styles.title} ${styles.gwOverrideDefaultMargin}`}>{title}</h3>
            <div className={styles.row} >
                <div >
                    <h3 className={`${styles.title1} ${styles.gwOverrideDefaultMargin}`}>{title1}</h3>
                </div>
                <div><h3 className={`${styles.title1} ${styles.gwOverrideDefaultMargin}`}>{title2}</h3></div>
            </div>
        </React.Fragment>
    );
}

const getLiabilityHeaderSection = (title) => {
    return (isOpen) => (
        <React.Fragment>
            <Chevron isOpen={isOpen} align="right" className={styles.chevronStyle} />
            <h3 className={`${styles.title} ${styles.gwOverrideDefaultMargin}`}>{title}</h3>
            
        </React.Fragment>
    );
}

function initialiseVM(submissionVM) {
    const vm = removeInitialData(submissionVM);
    vm.bindData.value = vm.bindData.value || {};
    vm.bindData.billingAddress.value = vm.bindData.billingAddress.value
        || vm.baseData.policyAddress.value;
    vm.bindData.contactEmail.value = vm.bindData.contactEmail.value
        || vm.baseData.accountHolder.emailAddress1.value;

    return vm;
}

function PolicyInformationPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [isSameBillingAddress, updateIsSameBillingAddress] = useState(true);
    const [isVmInitialized, updateIsVmInitialized] = useState(false);
    const localeService = ServiceManager.getService('locale-service');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate } = useValidation('PolicyInformationPage');

    const currencyFormatter = Intl.NumberFormat("en-CA", {style: "currency", currency: "CAD"});
    const [liabilityPremium, updateLiabilityPremium] = useState('');

    const DocumentPageService = {
        downloadDocument: GatewayDocumentService.downloadDocument
    };

    useEffect(() => {
        if (!isVmInitialized) {
            let nextSubmissionVM = viewModelService.clone(submissionVM);
            nextSubmissionVM = initialiseVM(nextSubmissionVM);

            if (!_.isEqual(submissionVM.value, nextSubmissionVM.value)) {
                updateWizardData(nextSubmissionVM);
            }
            updateIsVmInitialized(true);
        }
    }, [isVmInitialized, submissionVM, updateWizardData, viewModelService]);

    const handleNext = useCallback(async () => {
        if (isSameBillingAddress) {
            const policyAddress = _.get(submissionVM, 'baseData.policyAddress.value');
            _.set(submissionVM, 'bindData.billingAddress.value', policyAddress);
        }
        submissionVM.value = await LoadSaveService.updateQuotedSubmissionWithLOBData(submissionVM.value, authHeader);
        return submissionVM;
    }, [LoadSaveService, authHeader, isSameBillingAddress, submissionVM]);

    const generateQuoteDocument = useCallback(
        () => {
            SubmissionService.fetchQuoteDocument(submissionVM.value.quoteID, submissionVM.baseData.isIssuanceJob.value, authHeader).then((response) => {
                if(response.isAvailable){
                    //var downloadUrl = $scope.xCenterDownloadURI + '/' + response.publicID + '?token=' + response.sessionID + '&portalRoute=' + $scope.documentNotFoundURL;
                    //window.open(downloadUrl, '_blank');
                    var downloadUrl = DocumentPageService.downloadDocument(
                        response.publicID,
                        response.sessionID
                    );
                    window.open(downloadUrl, '_blank');
                } else {
                    ModalNextProvider.showAlert({
                        title: messages.farmDocumentGenerationInProgress,
                        message: messages.farmDocumentGenerationInProgressConf,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: 'Ok'
                    }).catch(_.noop);
                }
            }).catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.farmDocumentGenerationError,
                    message: messages.farmDocumentGenerationErrorConf,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: 'Ok'
                }).catch(_.noop);
            });
        },
        [submissionVM, authHeader, DocumentPageService]
    );

    const shouldShowPriorCarrierElements = (path, valueToMatch = true) => {
        const value = _.get(submissionVM, path);
        return value.code !== valueToMatch;
    };


    const generatePriorCarrierElementsOverride = () => {
        const code = 'NOPRIOR';
        const priorDetailsVisible = shouldShowPriorCarrierElements('baseData.priorCarName.value', code);
        return {
            priorPolicy: {
                visible: priorDetailsVisible
            },
            priorExpirationDate: {
                visible: priorDetailsVisible
            },

        };
    };
    const getLimitDeductibleValue = (terms, type) => {
        if (!_.isEmpty(terms)) {
            const termValue = terms.find((term) => term.name === type)
            return termValue !== undefined ? termValue.chosenTermValue : '-';
        }
        return "-";
    };

    const getCoverage = useCallback((additionalCoverages) => {
        return additionalCoverages.map((coverage) => {
            return {
                name: coverage.name,
                limit: getLimitDeductibleValue(coverage.terms, "Limit"),
                deductible: getLimitDeductibleValue(coverage.terms, "Deductible"),
                premium: coverage.amount !== undefined ? coverage.amount.amount : '-',
            };
        });
    }, []);

    const getLibCoverage = useCallback(() => {
        var offering = submissionVM.baseData.isFarmLineExist.value ? submissionVM.lobData.farm.offerings.value : submissionVM.lobData.homeowners.offerings.value ;
        if(!offering[0].coverages.isLiabilityLinePresent) {
            return [{}];
        }
        return offering
            .flatMap(offering => offering.coverages.liablityLineCoverages)
            // .filter(p =>  p.amount !== undefined)
            .map((coverage) => {

                return {
                    name: coverage.name,
                    limit: getLimitDeductibleValue(coverage.terms, "Limit"),
                    deductible: getLimitDeductibleValue(coverage.terms, "Deductible"),
                    premium: coverage.amount !== undefined ? coverage.amount.amount : '-',
                };
            });
    }, []);

    const generateCoverageAccordionHeaderOverride = useCallback(() => {
        if(!submissionVM.baseData.isResintialLineExist.value){
            return Object.assign({}, []);
        }
        const offerings = _.get(submissionVM, 'lobData.homeowners.offerings.value');
        const coverages = offerings.flatMap((offering) => offering.coverages.dwellingCoverages.map(dwe => dwe));
        const overrides = coverages.map((dwe, index) => {
            const headName = 'Location # ' + dwe.dwellingLocation
            const title1 = 'Package: ' + dwe.dwelingName;
            const title2 = `Premium:${ currencyFormatter.format(dwe.premium.amount)}`;
            const coverableVM = submissionVM.lobData.homeowners.allCoverables.children[index];
            return {
                [`coverageInfo${index}`]: {
                    data: getCoverage(dwe.additionalCoverages),
                },
                [`coverageDetailsContainer${index}`]: {
                    header: getHeaderSection(headName, title1,title2)
                },
                [`policyDiscounts${index}`]: {
                    data: dwe.discounts,
                    value: dwe.discounts,
                    visible: !_.isEmpty(dwe.discounts)
                },
                [`packageAdditionalInterestTable${index}`]:{
                    submissionVM: submissionVM,
                    updateWizardData: updateWizardData,
                    coverableVM,
                    packageIndex: index,
                    path: `lobData.homeowners.allCoverables.children.${index}`,
                    schedulesPath: `lobData.homeowners.allCoverables.children.${index}.scheduleItems`
                }
            }
        });
        return Object.assign({}, ...overrides);

    }, [])

    const generateFarmCoverageAccordionHeaderOverride = useCallback(() => {
        if(!submissionVM.baseData.isFarmLineExist.value){
            return Object.assign({}, []);
        }
        const offerings = _.get(submissionVM, 'lobData.farm.offerings.value');
        const coverages = offerings.flatMap((offering) => offering.coverages.dwellingCoverages.map(dwe => dwe));
        const overrides = coverages.map((dwe, index) => {
            const headName = 'Location # ' + dwe.dwellingLocation
            const title1 = 'Package: ' + dwe.dwelingName;
            const title2 = dwe.premium ? `Premium:${ currencyFormatter.format(dwe.premium.amount)}` : '';
            const coverableVM = submissionVM.lobData.farm.coverables.children[index];            
            return {
                [`farmCoverageInfo${index}`]: {
                    data: getCoverage(dwe.additionalCoverages),
                },
                [`farmCoverageDetailsContainer${index}`]: {
                    header: getHeaderSection(headName, title1,title2)
                },
                [`farmLevelDiscounts${index}`]: {
                    data: dwe.discounts,
                    value: dwe.discounts,
                    visible: !_.isEmpty(dwe.discounts)
                },
                [`farmPackageAdditionalInterestTable${index}`]:{
                    submissionVM: submissionVM,
                    updateWizardData: updateWizardData,
                    coverableVM,
                    packageIndex: index,
                    path: `lobData.farm.coverables.children.${index}`,
                    schedulesPath: `lobData.farm.coverables.children.${index}.scheduleItems`
                }
            }
        });
        return Object.assign({}, ...overrides);

    }, [])

    const liabilityTitle2 = `Premium:${ currencyFormatter.format(liabilityPremium)}`;
    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        sameBillingAddress: {
            value: isSameBillingAddress,
            onValueChange: updateIsSameBillingAddress
        },
        billingAddressFields: {
            visible: !isSameBillingAddress
        },
        policyCommonDetailsForCurrentPeriod: {
            submissionVM: submissionVM
        },
        libcoverageInfo: {
            data: getLibCoverage(),
        },
        liabilityCoverageDetailsContainer : {
            header: getLiabilityHeaderSection("Liability Line Coverages")
        },        
        liabilityDetailsCardSection:{
            header: getHeaderSection("Liability and Extensions", '', liabilityTitle2) //getLiabilityHeaderSection("Liability and Extensions")
        },
        liabilityComponentSection:{
            value: submissionVM.value,
            updateLiabilityPremium: updateLiabilityPremium
        },
        ...generatePriorCarrierElementsOverride(),
        ...generateCoverageAccordionHeaderOverride(),
        ...generateFarmCoverageAccordionHeaderOverride()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            updateIsSameBillingAddress,
            generateQuoteDocument: generateQuoteDocument,
            updateLiabilityPremium : updateLiabilityPremium
        },

        resolveComponentMap: {
            policycommondetailscomponent: PolicyDetailsComponent,
            coveragetablecomponent: CoverageTable,
            liabilitycomponent: PolicyLiabilityComponent,
            discountscomponent: DiscountComponent,
            additionalinteresttable: AdditionalInterestTable

        },
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [submissionVM, overrideProps]
    );

    return (
        <WizardPage onNext={handleNext} disableNext={!isComponentValid || !submissionVM.aspects.valid || !submissionVM.aspects.subtreeValid}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

PolicyInformationPage.propTypes = wizardProps;
export default PolicyInformationPage;
